import { Ref, computed } from "vue"
import { useQuery, useQueryClient } from "@tanstack/vue-query"
import { HuonekorttiResponse } from "@/api/models/huonekortti-response"
import http from "@/http-common"
import bim, { bimKaytossa } from "@/bim-http"
import { BimCompliance, BimMalli, BimTila } from "@/types/bim"
import { useHanke } from "@/methods/hankeComposables"

/** Vahdin API:n sivukoko huonekorteille. 100 on rajapinnan maksimi. */
const SIVUKOKO = 100

/** Mille hankkeille malli on olemassa. Kysytaan kerran ja pidetaan pitkaan:
 *  mallirekisteri muuttuu vain kun IFC vaihtuu. */
export function useBimMallit() {
    const { data: mallit, isLoading: loading } = useQuery({
        queryKey: ["bim", "mallit"],
        queryFn: async (): Promise<BimMalli[]> => (await bim.get("/mallit")).data,
        enabled: bimKaytossa,
        // Kaareen alhaallaolo ei ole virhe vaan tila: liitospinnat piiloutuvat.
        retry: false,
        staleTime: 5 * 60 * 1000,
    })

    return { mallit, loading }
}

/** Vertaa hankkeen huonekortit IFC-malliin.
 *
 *  Suunta on "client tyontaa": Vue hakee huonekortit Vahdin API:sta omalla
 *  Auth0-tokenillaan ja lahettaa ne kaareelle, joka laskee vertailun. Nain
 *  Auth0 pysyy kokonaan siella missa se jo toimii eika Pythoniin tarvita
 *  token- eika sivutuslogiikkaa.
 *
 *  Vahti on auktoritatiivinen: tulos on read-only-vertailu eika mikaan tassa
 *  kirjoita Vahtiin.
 */
export function useBimVertailu(hankeId: Ref<number | undefined>) {
    const queryClient = useQueryClient()
    const { mallit } = useBimMallit()
    const { hanke } = useHanke(hankeId)

    /** Vertailu on saatavilla jos kaare tuntee taman hankkeen TAI tarjoaa
     *  oletusmallin. Oletusmalli on demovarmistus: se toimii silla
     *  hankkeella joka sattuu olemaan auki. */
    const malli = computed(() =>
        mallit.value?.find((m) => m.hankeId === hankeId.value)
        ?? mallit.value?.find((m) => m.oletus)
    )

    const vastuuyksikkoIdt = computed(
        () => hanke.value?.vastuuyksikot?.map((v) => v.id) ?? []
    )

    const kysely = useQuery({
        queryKey: computed(() => ["bim", "vertailu", hankeId.value, vastuuyksikkoIdt.value]),
        // Haku on kirjoitettu queryFn:n sisaan eika omaksi apufunktioksi, jotta
        // sulkeuma viittaa vain arvoihin jotka ovat queryKeyssa — muuten
        // valimuisti voisi palauttaa vanhan vastuuyksikkojoukon tuloksen.
        queryFn: async (): Promise<BimCompliance> => {
            // Kaikki hankkeen huonekortit sivuttaen. Silmukka on kirjoitettava
            // oikein heti, vaikka demohankkeessa on vain 24 korttia:
            // vaillinainen lista nakyisi vertailussa puuttuvina tiloina, eli
            // vaarana havaintona eika virheena.
            const huonekortit: HuonekorttiResponse[] = []

            for (let sivu = 1; ; sivu++) {
                const vastaus = await http.get("/huonekortit", {
                    params: {
                        vastuuyksikot: vastuuyksikkoIdt.value.join(","),
                        // PAKOLLINEN: oletus on false, ja ilman tata koko
                        // varustevertailu palauttaisi tyhjaa ilman virhetta.
                        nimikkeet: true,
                        page: sivu,
                        per_page: SIVUKOKO,
                    },
                })
                const era = (vastaus.data ?? []) as HuonekorttiResponse[]
                huonekortit.push(...era)
                if (era.length < SIVUKOKO) break
            }

            const vastaus = await bim.post("/vertailu", {
                hankeId: hankeId.value ?? null,
                huonekortit,
            })

            return vastaus.data
        },
        enabled: computed(() =>
            bimKaytossa && !!malli.value && vastuuyksikkoIdt.value.length > 0
        ),
        retry: false,
        staleTime: 5 * 60 * 1000,
    })

    const compliance = kysely.data

    /** Tilatunnus -> mallin tila. Tilat-taulukko ja huonekortin mallilohko
     *  hakevat talla; tilatunnus on ainoa tunniste jonka molemmat tuntevat. */
    const tilaTunnuksella = computed(() => {
        const kartta = new Map<string, BimTila>()
        for (const t of compliance.value?.tilat ?? []) kartta.set(t.tilatunnus, t)

        return kartta
    })

    /** Nayttaako Vahti BIM-liitospinnat lainkaan. False = kaare alhaalla,
     *  hankkeelle ei ole mallia tai liitos ei ole konfiguroitu -> kaikki
     *  liitospinnat piiloutuvat hiljaa ja muut hankkeet nayttavat entiselta. */
    const saatavilla = computed(() => !!compliance.value && !kysely.error.value)

    const paivita = () =>
        queryClient.invalidateQueries({ queryKey: ["bim", "vertailu", hankeId.value] })

    return {
        compliance,
        malli,
        tilaTunnuksella,
        saatavilla,
        loading: kysely.isLoading,
        fetching: kysely.isFetching,
        error: kysely.error,
        paivita,
    }
}
